import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Download, Users, CurrencyCircleDollar, GenderIntersex, Briefcase, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLORS = ["#0B2027", "#285943", "#C05A42", "#D4A373", "#6B7280", "#8B5CF6", "#F59E0B"];
const GENDER_COLORS = { F: "#EC4899", M: "#3B82F6", "Diğer": "#9CA3AF" };
const GENDER_LABELS = { F: "Kadın", M: "Erkek", "Diğer": "Belirtilmemiş" };

export default function Reports() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [extended, setExtended] = useState(null);
  const [users, setUsers] = useState([]);
  const [normReport, setNormReport] = useState(null);
  const [departures, setDepartures] = useState(null);
  const [turnoverPeriod, setTurnoverPeriod] = useState("yearly");

  const canSeeExtended = ["admin", "hr", "gm"].includes(user?.role);

  useEffect(() => {
    api.get("/reports/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/personnel").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/reports/norm-vs-actual").then((r) => setNormReport(r.data)).catch(() => {});
    api.get("/reports/departures").then((r) => setDepartures(r.data)).catch(() => {});
    if (canSeeExtended) {
      api.get("/reports/extended").then((r) => setExtended(r.data)).catch(() => {});
    }
  }, [canSeeExtended]);

  const exportCSV = () => {
    if (!users.length) return;
    const cols = ["first_name", "last_name", "email", "phone", "department", "position", "role", "hire_date", "birth_date"];
    const rows = [cols.join(",")].concat(users.map((u) => cols.map((c) => `"${(u[c] ?? "").toString().replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "personel.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const exportExcel = async () => {
    try {
      const r = await api.get("/reports/export?fmt=xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rapor_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapor Excel olarak indirildi");
    } catch { toast.error("Excel indirilemedi"); }
  };

  if (!summary) return <div className="text-muted-foreground">Yükleniyor…</div>;

  const genderData = extended ? Object.entries(extended.gender_counts || {}).map(([k, v]) => ({ name: GENDER_LABELS[k] || k, key: k, value: v })).filter((d) => d.value > 0) : [];
  const tenureData = extended ? Object.entries(extended.tenure_buckets || {}).map(([k, v]) => ({ name: `${k} yıl`, value: v })) : [];
  const monthlyAnnual = extended?.monthly_annual_leave;
  const REASON_COLORS = { resignation: "#C05A42", termination: "#0B2027", retirement: "#285943", other: "#6B7280" };
  const turnoverReasons = summary.turnover_reasons;
  const tPeriod = turnoverPeriod === "monthly" ? summary.turnover_monthly : summary.turnover_yearly;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Analiz</div>
          <h1 className="font-display text-3xl font-bold mt-1">Raporlar</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="export-csv"><Download size={16} className="mr-1" />CSV İndir</Button>
          <Button onClick={exportExcel} data-testid="export-xlsx"><FileXls size={16} className="mr-1" />Excel İndir</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBlock label="Toplam" value={summary.total} />
        <StatBlock label="Bugün İzinli" value={summary.on_leave_today} />
        <StatBlock label="Bekleyen İzin" value={summary.pending_leaves} />
        <StatBlock label="Devir Oranı %" value={summary.turnover_percent} />
      </div>

      {turnoverReasons && tPeriod && (
        <Card data-testid="turnover-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 font-display text-lg font-bold">
                <Users size={20} weight="duotone" className="text-primary" />
                Devir / Ayrılan Personel
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                  <button type="button" onClick={() => setTurnoverPeriod("monthly")} data-testid="turnover-period-monthly"
                    className={`px-3 py-1.5 transition-colors ${turnoverPeriod === "monthly" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>Bu Ay ({summary.turnover_monthly?.month})</button>
                  <button type="button" onClick={() => setTurnoverPeriod("yearly")} data-testid="turnover-period-yearly"
                    className={`px-3 py-1.5 transition-colors ${turnoverPeriod === "yearly" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>Son 12 Ay</button>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline">Ayrılan: {tPeriod.exits ?? 0}</Badge>
                  <Badge variant="outline">Ort. kadro: {tPeriod.avg_headcount ?? 0}</Badge>
                  <Badge variant="destructive" data-testid="turnover-rate-badge">Devir Oranı %{tPeriod.percent}</Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(tPeriod.labels).map(([key, label]) => (
                <div key={key} className="rounded-lg border border-border p-4" data-testid={`turnover-reason-${key}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: REASON_COLORS[key] }} />
                    <div className="text-xs text-muted-foreground">{label}</div>
                  </div>
                  <div className="font-display text-2xl font-bold mt-1">{tPeriod.counts?.[key] ?? 0}</div>
                </div>
              ))}
            </div>

            <div className="text-[11px] text-muted-foreground">
              Devir Oranı = dönemde ayrılan çalışan / ortalama çalışan sayısı × 100. Ortalama kadro = (dönem başı + dönem sonu) / 2.
            </div>

            {departures?.departures?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left p-2">Personel</th>
                      <th className="text-left p-2">Departman</th>
                      <th className="text-left p-2">Görev</th>
                      <th className="text-left p-2">Neden</th>
                      <th className="text-right p-2">Ayrılma Tarihi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departures.departures.map((d) => (
                      <tr key={d.id} className="border-t border-border" data-testid={`departure-row-${d.id}`}>
                        <td className="p-2">{d.name}</td>
                        <td className="p-2">{d.department || "-"}</td>
                        <td className="p-2">{d.position || "-"}</td>
                        <td className="p-2">
                          <Badge variant="outline" className="text-[10px]" style={{ borderColor: REASON_COLORS[d.reason], color: REASON_COLORS[d.reason] }}>
                            {d.reason_label}
                          </Badge>
                        </td>
                        <td className="p-2 text-right font-mono">{d.departure_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(!departures || departures.departures?.length === 0) && (
              <div className="text-sm text-muted-foreground py-2">Son 12 ayda ayrılan personel kaydı yok.</div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card><CardContent className="p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Departman Bazlı</div>
          <div className="h-72"><ResponsiveContainer><BarChart data={Object.entries(summary.dept_counts).map(([name, value]) => ({ name, value }))}><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Bar dataKey="value" fill="#0B2027" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer></div>
        </CardContent></Card>
        <Card><CardContent className="p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Yaş Dağılımı</div>
          <div className="h-72"><ResponsiveContainer><PieChart><Pie data={Object.entries(summary.age_buckets).map(([name, value]) => ({ name, value }))} dataKey="value" nameKey="name" outerRadius={90} label>{Object.keys(summary.age_buckets).map((bucketName) => <Cell key={`age-${bucketName}`} fill={COLORS[Object.keys(summary.age_buckets).indexOf(bucketName) % COLORS.length]} />)}</Pie><Tooltip /><Legend /></PieChart></ResponsiveContainer></div>
        </CardContent></Card>
      </div>

      {canSeeExtended && extended && (
        <>
          {/* Aylık Yıllık İzin Ödemesi Özet Kartı */}
          <Card data-testid="monthly-annual-pay-card" className="border-primary/40">
            <CardContent className="p-6">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <CurrencyCircleDollar size={22} weight="duotone" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bu Ay Onaylanan Yıllık İzin Ödemesi</div>
                    <div className="font-display text-3xl font-bold mt-1" data-testid="monthly-annual-pay-total">
                      {Number(monthlyAnnual?.total_pay || 0).toLocaleString("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {monthlyAnnual?.month} • {monthlyAnnual?.people || 0} personel • {monthlyAnnual?.total_days || 0} gün
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 min-w-[300px]">
                  <MiniStat label="Personel" value={monthlyAnnual?.people || 0} />
                  <MiniStat label="Toplam Gün" value={monthlyAnnual?.total_days || 0} />
                  <MiniStat label="Onaylı İzin" value={monthlyAnnual?.people || 0} />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Cinsiyet Dağılımı */}
            <Card data-testid="gender-distribution-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <GenderIntersex size={18} weight="duotone" className="text-primary" />
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cinsiyet Dağılımı</div>
                </div>
                {genderData.length === 0 ? (
                  <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">Cinsiyet bilgisi henüz girilmemiş</div>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={genderData} dataKey="value" nameKey="name" outerRadius={100} label={(e) => `${e.name}: ${e.value}`}>
                          {genderData.map((d) => (
                            <Cell key={`gender-${d.key}`} fill={GENDER_COLORS[d.key] || "#9CA3AF"} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Kıdem/Çalışma Süresi Dağılımı */}
            <Card data-testid="tenure-distribution-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase size={18} weight="duotone" className="text-primary" />
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Kıdem / Çalışma Süresi</div>
                </div>
                <div className="h-72">
                  <ResponsiveContainer>
                    <BarChart data={tenureData}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#285943" radius={[6, 6, 0, 0]}>
                        {tenureData.map((_, idx) => (
                          <Cell key={`tenure-${idx}`} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {normReport && (
        <Card data-testid="norm-vs-actual-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 font-display text-lg font-bold">
                <Users size={20} weight="duotone" className="text-primary" />
                Norm Kadro vs Mevcut Kadro
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline">Hedef: {normReport.totals.target}</Badge>
                <Badge>Mevcut: {normReport.totals.actual}</Badge>
                <Badge variant={normReport.totals.gap === 0 ? "default" : normReport.totals.gap > 0 ? "destructive" : "secondary"}>
                  Fark: {normReport.totals.gap > 0 ? `+${normReport.totals.gap} eksik` : normReport.totals.gap < 0 ? `${normReport.totals.gap} fazla` : "Denge"}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatBlock label="Norm Pozisyon" value={normReport.rows.filter((r) => r.status !== "unlisted").length} />
              <StatBlock label="Eksik Kadro" value={normReport.totals.understaffed_count} />
              <StatBlock label="Fazla Kadro" value={normReport.totals.overstaffed_count} />
              <StatBlock label="Norm Dışı" value={normReport.totals.unlisted_count} />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left p-2">Departman</th>
                    <th className="text-left p-2">Pozisyon</th>
                    <th className="text-right p-2">Hedef</th>
                    <th className="text-right p-2">Mevcut</th>
                    <th className="text-right p-2">Fark</th>
                    <th className="text-left p-2">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {normReport.rows.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="p-2">{r.department}</td>
                      <td className="p-2">{r.position}</td>
                      <td className="p-2 text-right font-mono">{r.target_count}</td>
                      <td className="p-2 text-right font-mono">{r.actual_count}</td>
                      <td className={`p-2 text-right font-mono ${r.gap > 0 ? "text-destructive" : r.gap < 0 ? "text-warning" : "text-muted-foreground"}`}>
                        {r.gap > 0 ? `+${r.gap}` : r.gap}
                      </td>
                      <td className="p-2">
                        {r.status === "ok" && <Badge variant="default" className="text-[10px]">Tam</Badge>}
                        {r.status === "understaffed" && <Badge variant="destructive" className="text-[10px]">Eksik</Badge>}
                        {r.status === "overstaffed" && <Badge variant="secondary" className="text-[10px]">Fazla</Badge>}
                        {r.status === "unlisted" && <Badge variant="outline" className="text-[10px]">Norm Dışı</Badge>}
                      </td>
                    </tr>
                  ))}
                  {normReport.rows.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Henüz norm kadro tanımı yok.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {normReport.departments.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Departman Özeti</div>
                <div className="h-72">
                  <ResponsiveContainer>
                    <BarChart data={normReport.departments.map((d) => ({ name: d.department, Hedef: d.target, Mevcut: d.actual }))}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Hedef" fill="#285943" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="Mevcut" fill="#C05A42" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatBlock({ label, value }) {
  return <div className="stat-card"><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div><div className="font-display text-3xl font-bold mt-2">{value}</div></div>;
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-xl font-bold mt-1">{value}</div>
    </div>
  );
}
